import {
  ChatMessage,
  ChatMessageActions,
  ChatMessageContent,
  ChatMessageHeader,
  ChatMessageSender,
  ChatMessageTime,
} from '@glade-ai/chat-plugin';
import {
  AvatarFallback,
  AvatarImage,
  AvatarInitials,
  BadgeAvatar,
  BadgedAvatar,
  BaseAvatar,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  Icon,
  IconButton,
  type NumericSize,
  cn,
} from '@glade-ai/ui';
import pluralize from 'pluralize';
import { type FC, type RefObject, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type Descendant } from 'slate';

import BroadcastLock from '@/components/BroadcastLock';
import CustomLink from '@/components/CustomLink';
import GeneratedByNoodleAi from '@/components/GeneratedByNoodleAi';
import { gladeImgSrc } from '@/components/Icons/Noodle';
import { gladeAiImgSrc } from '@/components/Icons/NoodleAI';
import CommentContent from '@/components/Message/CommentContent';
import ProgressIndicator from '@/components/ProgressIndicator';
import Shimmer from '@/components/Shimmer';
import Spacer from '@/components/Spacer';
import { useUserImageColor, userIsAnonymous } from '@/components/UserImage/UserImage';
import { NOODLE_CREATOR_PERSON_ID } from '@/configuration/client';
import { ConversationComposeBox } from '@/containers/chat-composeboxes/ConversationComposeBox';
import { useMessageActions } from '@/containers/chat-hooks';
import { OpenThreadButton } from '@/containers/chat-messages/OpenThreadButton';
import { type ChatMessage as ChatMessageType } from '@/containers/chats/ConversationChat/types';
import { UsersAvatarGroup } from '@/containers/UsersAvatarGroup';
import * as format from '@/format';
import nameToInitials from '@/helpers/nameToInitials';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import Upsell from '@/lib/UserProfile/components/Upsell';
import { mixpanelTrack } from '@/providers/Mixpanel';
import TeamsContext from '@/providers/Teams/TeamsContext';
import { deleteMessage, getAllParticipantsOfBroadcast, postNewMessage, updateMessage } from '@/tsClient';
import { MessageType } from '@/typings/api-models';
import { NoodleProductTypes } from '@/typings/graphql-models';

import s from './CommentMessage.module.scss';
import { ConversationMessageInFlowThread } from './ConversationMessageInFlowThread';
import { type ConversationMessage as ConversationMessageType } from './types';

type ConversationMessageProps = {
  refetchMessage?: () => Promise<void>;
  isThreadDetail?: boolean;
  isThreadComment?: boolean;
  isEmbeddedFromModal?: boolean;
  isChatMessage?: {
    isAutomated?: boolean;
    isAutomatedFromNoodle?: boolean;
    isPaid?: boolean;
    isBroadcastMessage?: boolean;
    isByteMessage?: boolean;
    isResponding?: boolean;
  };
  creatorPersonId: string;
  creatorSlug: string;
  product?: NonNullable<Parameters<typeof BroadcastLock>[0]['product']>;
  productSlug?: string | null;
  onPostNewMessage?: (comment: Awaited<ReturnType<typeof postNewMessage>>) => void;
  onDeleteMessage?: (messageId: string, subMessageId: string | undefined) => void;
  onOpenThread?: (threadMessageId: string) => void;
  onCloseThread?: () => void;
  message: ConversationMessageType;
  messageId?: string;
  isReadOnly?: boolean;
  canPinMessages: boolean;
  canDeleteMessages: boolean;
  canEditMessages?: boolean;
  canReplyToMessages: boolean;
  usesSecondaryActionReply?: boolean;
  isConversationThread?: boolean;
  replyInline?: boolean;
  person?: {
    id: string;
    creator?: {
      slug: string;
    } | null;
  };
  onEditClick?: () => void;
  setCurrentMessage?: (message: ChatMessageType) => void;
  currentMessage?: ChatMessageType | null | undefined;
  containerRef?: RefObject<HTMLDivElement>;
  customerId?: string;
  reloadConversation?: () => Promise<void>;
  isWorkflowComment?: boolean;
};

type CommentMessageAvatarProps = Pick<ConversationMessageProps, 'isChatMessage' | 'message'> & {
  user: { id: string } | null;
  size?: NumericSize;
};

const CommentMessageAvatar: FC<CommentMessageAvatarProps> = ({ message, isChatMessage, user, size }) => {
  const { isAutomated, isAutomatedFromNoodle, isBroadcastMessage, isByteMessage, isPaid } = isChatMessage || {};
  const userDisplayName = format.messageOwnerName({ owner: message.owner, user });
  const isAnonymous = userIsAnonymous(userDisplayName);
  const userColors = useUserImageColor({ color: message.owner?.primaryColour?.hex, isAnonymous });
  return (
    <BadgedAvatar size={size} badgeScale='lg'>
      <BaseAvatar>
        <AvatarFallback style={userColors}>
          <span className='sr-only'>{userDisplayName}</span>
          <AvatarInitials initials={nameToInitials(isAutomatedFromNoodle ? 'Glade AI' : userDisplayName)} />
        </AvatarFallback>
        <AvatarImage src={isAutomatedFromNoodle ? gladeImgSrc : message.owner?.image?.url} />
      </BaseAvatar>
      {(message.isPinned || isBroadcastMessage || isByteMessage || isAutomated || isPaid) && (
        <BadgeAvatar
          className={cn(isAutomated ? 'bg-background' : isPaid ? 'bg-success' : 'bg-primary', 'text-foreground')}
        >
          <AvatarFallback>
            {isAutomated ? (
              'AI'
            ) : (
              <Icon
                icon={
                  isBroadcastMessage
                    ? 'ph--broadcast--fill'
                    : isByteMessage
                      ? 'ph--cookie--fill'
                      : isPaid
                        ? 'ph--currency-dollar-simple--regular'
                        : 'ph--push-pin--fill'
                }
                size='full'
              />
            )}
          </AvatarFallback>
          {isAutomated && <AvatarImage src={gladeAiImgSrc} />}
        </BadgeAvatar>
      )}
    </BadgedAvatar>
  );
};

const CREATORS_WITHOUT_UPSELL = ['peterdanyliv'];

/** Displays a single conversation message with avatar, content, and action menu. */
export const ConversationMessage: FC<ConversationMessageProps> = ({
  message,
  onEditClick,
  isThreadDetail,
  isEmbeddedFromModal,
  onPostNewMessage,
  creatorPersonId,
  creatorSlug,
  product,
  productSlug,
  messageId,
  onDeleteMessage,
  onOpenThread,
  onCloseThread,
  isThreadComment,
  isReadOnly = false,
  replyInline = false,
  person,
  refetchMessage,
  isChatMessage,
  canPinMessages,
  canDeleteMessages,
  canEditMessages,
  canReplyToMessages,
  customerId,
  reloadConversation,
  isWorkflowComment,
  usesSecondaryActionReply,
  isConversationThread,
  setCurrentMessage,
  currentMessage,
}) => {
  const [user] = useUser();
  const { currentTeamOwner } = useContext(TeamsContext);
  const { fetchingState, getData: postCommentFn } = useNoodleApi(postNewMessage, {
    healthMonitor: { name: 'send-message' },
  });
  const { getData: deleteCommentFn } = useNoodleApi(deleteMessage);
  const { getData: updateMessageFn } = useNoodleApi(updateMessage);
  const [isUpdatingPinState, setIsUpdatingPinState] = useState(false);

  const commentType = message?.type;
  const isCreator = !!currentTeamOwner && creatorSlug === currentTeamOwner.slug;

  // Check if we're in any modal context (thread modal or embedded modal)
  const isInModal = isThreadDetail || isEmbeddedFromModal || false;

  const handleSend = async ({
    text,
    medias = [],
    handbooks = [],
  }: {
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
  }): Promise<void> => {
    if ((text?.length || medias?.length || handbooks?.length) && productSlug) {
      const newComment = await postCommentFn({
        creatorSlug,
        handbooks,
        medias,
        messageId,
        messageType: isWorkflowComment ? MessageType.WorkflowComment : MessageType.Comment,
        productSlug,
        sendAsCreator: isCreator,
        text: text ? { children: text } : null,
      });
      if (newComment?.data) {
        onPostNewMessage?.(newComment.data);
        mixpanelTrack('Added reply', {
          medias,
          parent: messageId,
          text,
        });
      } else {
        mixpanelTrack('(Creator) Added reply', {
          medias,
          parent: messageId,
          text,
        });
      }
    }
  };

  const {
    data: participantsData,
    fetchingState: fetchingParticipantsState,
    getData: getAllParticipantsOfBroadcastFn,
  } = useNoodleApi(getAllParticipantsOfBroadcast);

  useEffect(() => {
    if (messageId && creatorSlug && productSlug) {
      getAllParticipantsOfBroadcastFn({
        creatorSlug,
        messageId,
        productSlug,
      });
    }
  }, [messageId, creatorSlug, getAllParticipantsOfBroadcastFn, productSlug]);

  const handleEdit = useCallback((): void => {
    if (!setCurrentMessage || !message) return;

    setCurrentMessage({
      ...message,
      editedAt: message.editedAt ?? null,
      isAIGenerated: message.isAIGenerated ?? false,
      numChildren: message.numChildren ?? 0,
      participants: message.participants ?? undefined,
    });
  }, [message, setCurrentMessage]);

  const handleDelete = useCallback(async (): Promise<void> => {
    if (message.id) {
      await deleteCommentFn({ creatorSlug, messageId: message.id, productSlug: productSlug ?? 'no-product-slug' });
      onDeleteMessage?.(message.id, undefined);
    }
  }, [message.id, creatorSlug, deleteCommentFn, onDeleteMessage, productSlug]);

  useEffect(() => {
    if (window.location.hash) {
      const elementId = window.location.hash.substring(1);
      if (elementId === message.id) {
        const element = document.getElementById(elementId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [message]);

  const updatePinState = useCallback(async (): Promise<void> => {
    setIsUpdatingPinState(true);
    if (productSlug) {
      await updateMessageFn({
        ...message,
        creatorSlug,
        isPinned: !message.isPinned,
        messageId: message.id,
        productSlug,
      });
      await refetchMessage?.();
    }
    setIsUpdatingPinState(false);
  }, [message, creatorSlug, productSlug, refetchMessage, updateMessageFn]);

  const handleDeleteSubComment =
    (commentIdProp: string) =>
    (subCommentId: string): void => {
      onDeleteMessage?.(commentIdProp, subCommentId);
    };

  const childComments = [...(message?.children || [])];

  // Memoize unique owners from child comments to avoid O(n²) computation on every render
  const uniqueOwnersFromComments = useMemo(() => {
    const seen = new Set<string>();
    return childComments.filter((child) => {
      const id = child.owner?.id;
      if (id && !seen.has(id)) {
        seen.add(id);
        return true;
      }
      return false;
    });
  }, [childComments]);

  // Thread participants for the "Open Thread" button - uses message.participants if available,
  // otherwise derives from unique child comment owners
  const threadParticipants = useMemo(() => {
    const participantsFromMessage = message.participants || [];
    if (participantsFromMessage.length > 0) {
      return participantsFromMessage;
    }
    return uniqueOwnersFromComments.map((child) => ({
      color: child.owner?.primaryColour?.hex ?? null,
      id: child.owner?.id ?? '',
      logo: child.owner?.image?.url ?? null,
      name: child.owner?.name ?? null,
    }));
  }, [message.participants, uniqueOwnersFromComments]);

  const isPost = (type?: MessageType | null): boolean =>
    type === MessageType.PaidBroadcast || type === MessageType.FreeBroadcast || type === MessageType.CommunityPost;

  const canReply = canReplyToMessages ? !message.parent?.parent : false;
  const canSecondaryActionReply = canReplyToMessages && usesSecondaryActionReply;

  // For chat conversations (no productSlug), use participants from comment object if available
  // For product broadcasts, use participants from API
  const participantsFromComment =
    message.participants?.filter(
      (participant, index, self) => self.findIndex((p) => p.id === participant.id) === index,
    ) || [];
  const participantsFromApi =
    participantsData?.participants?.filter(
      (participant, index, self) => self.findIndex((p) => p.id === participant.id) === index,
    ) || [];

  // Use comment participants for chat conversations, API participants for product broadcasts
  const uniqueParticipants = isChatMessage && !productSlug ? participantsFromComment : participantsFromApi;

  const isOwnMessage = message.owner?.id === user?.id;
  const isPostType = isPost(commentType);
  const canEditMessage = canEditMessages && isOwnMessage && !isPostType;
  const canEditPost = !replyInline && (isCreator || isOwnMessage) && isPostType && !!onEditClick;

  const { actions: menuActions } = useMessageActions({
    callbacks: {
      onDelete: handleDelete,
      onEdit: canEditPost ? onEditClick : handleEdit,
      onPin: updatePinState,
      onReplyInMainChat: onCloseThread,
      onReplyInThread: onOpenThread ? () => onOpenThread(message.id) : undefined,
    },
    icons: {
      pin: message.isPinned ? 'ph--push-pin-slash--regular' : 'ph--push-pin--regular',
    },
    labels: {
      edit: canEditPost ? 'Edit post' : 'Edit message',
      pin: message.isPinned ? 'Unpin' : 'Pin',
    },
    message,
    permissions: {
      canDelete: canDeleteMessages && isOwnMessage,
      canEdit: canEditMessage || canEditPost,
      canPin: !replyInline && canPinMessages,
      canReplyInMainChat: isThreadDetail && !isThreadComment && !isOwnMessage,
      canReplyInThread: canSecondaryActionReply && (!isThreadDetail || !isThreadComment),
    },
    state: {
      isPinPending: isUpdatingPinState,
    },
  });

  const commentAvatar = (
    <CommentMessageAvatar
      message={message}
      user={user}
      size={isInModal ? 6 : 10}
      {...(isChatMessage && { isChatMessage })}
    />
  );
  const commentOwnerName = format.messageOwnerName({ owner: message?.owner, user });

  // Reply action for touch devices - opens thread if available
  const handleTapReply = useMemo(() => {
    if (canSecondaryActionReply && onOpenThread && (!isThreadDetail || !isThreadComment)) {
      return () => onOpenThread(message.id);
    }
    return undefined;
  }, [canSecondaryActionReply, onOpenThread, isThreadDetail, isThreadComment, message.id]);

  return (
    <ContextMenu>
      <div role='none' id={message.id}>
        {message.isContentHidden && !isThreadComment && (
          <BroadcastLock
            upgradeTiers={message.upgradeTiers || []}
            product={product}
            creatorSlug={creatorSlug}
            hasFreeAccess={message.isAvailableForFree}
            commentId={message.id}
            isCommunityPost={message.owner?.id !== creatorPersonId}
          />
        )}
        {message.isDeleted &&
          message.type !== MessageType.ProductDiscussion &&
          (message.children?.filter((c) => !c.isDeleted).length || 0) > 0 && (
            <div className={`body-sm ${s['comment-msg__deleted']}`}>
              <Icon icon='ph--trash--regular' size={5} />
              <span>This message has been deleted</span>
            </div>
          )}
        {!message.isDeleted && (
          <ContextMenuTrigger asChild>
            <ChatMessage onReply={handleTapReply}>
              {message?.owner?.id === creatorPersonId ? (
                <CustomLink to={`/${creatorSlug}`} className={s['comment-msg__user-logo-container']}>
                  {commentAvatar}
                </CustomLink>
              ) : (
                commentAvatar
              )}
              <ChatMessageContent className='relative z-0'>
                {(!message.isContentHidden || isThreadComment) && (
                  <ChatMessageHeader>
                    <ChatMessageSender>
                      {message?.owner?.id === creatorPersonId ? (
                        <CustomLink to={`/${creatorSlug}`}>{commentOwnerName}</CustomLink>
                      ) : (
                        commentOwnerName
                      )}
                    </ChatMessageSender>
                    <ChatMessageTime dateTime={message.createdAt} />
                    {isChatMessage?.isAutomated && message?.owner?.id !== NOODLE_CREATOR_PERSON_ID && (
                      <span className='gl-description'>Assisted by Glade AI</span>
                    )}
                    {message.editedAt && <span className='gl-description'>(edited)</span>}
                  </ChatMessageHeader>
                )}
                <CommentContent
                  comment={{
                    ...message,
                    attachments: message.attachments || [],
                    children: message.children || [],
                    medias: message.medias || [],
                    products: message.products || [],
                    taggedUsers: message.taggedUsers || [],
                  }}
                  customerId={customerId}
                  creator={{ personId: creatorPersonId, slug: creatorSlug }}
                  reloadConversation={reloadConversation}
                />

                {isChatMessage?.isAutomated && (
                  <GeneratedByNoodleAi creatorName={format.messageOwnerName({ owner: message?.owner, user })} />
                )}

                {/* Don't show UsersAvatarGroup for chat conversations - participants show in "Open Thread" button instead */}
                {!isChatMessage && (uniqueParticipants?.length || 0) > 0 && (
                  <UsersAvatarGroup users={uniqueParticipants || []} size={4} />
                )}

                {!isChatMessage && fetchingParticipantsState.isFetching && <ProgressIndicator />}

                {usesSecondaryActionReply && (message?.numChildren || 0) > 0 && onOpenThread && (
                  <OpenThreadButton
                    participants={threadParticipants}
                    replyCount={message.numChildren || 0}
                    onClick={() => onOpenThread(message.id)}
                    avatarSize={4}
                  />
                )}

                {isThreadDetail &&
                  message.canReply &&
                  isPost(commentType) &&
                  !CREATORS_WITHOUT_UPSELL.includes(creatorSlug) && (
                    <div className={s['comment-msg__upsells']}>
                      <Upsell creatorSlug={creatorSlug} type={NoodleProductTypes.Lite} isMinified />
                    </div>
                  )}
              </ChatMessageContent>
              {!message.isDeleted && menuActions.length > 0 && (
                <ChatMessageActions>
                  {menuActions.map((action) => (
                    <IconButton
                      key={action.label}
                      icon={action.isPending ? 'ph--circle-notch--regular' : action.icon}
                      iconClassName={action.isPending ? 'animate-spin' : undefined}
                      label={action.label}
                      onClick={action.onClick}
                      iconOnly
                      variant={action.variant === 'destructive' ? 'ghost-destructive' : 'ghost'}
                      size='sm'
                    />
                  ))}
                </ChatMessageActions>
              )}
            </ChatMessage>
          </ContextMenuTrigger>
        )}
        {(!message.isDeleted || (message.children?.filter((c) => !c.isDeleted).length || 0) > 0) &&
          (productSlug || isConversationThread) && (
            <>
              {isThreadDetail && (
                <div className={s['comment-msg__replies-header']}>
                  {childComments.length > 0 ? (
                    <>
                      {childComments.length} {pluralize('reply', childComments.length)}
                    </>
                  ) : (
                    <>
                      <Icon icon='ph--chat-circle--regular' size={4} />
                      Reply in thread
                    </>
                  )}
                </div>
              )}
              <ConversationMessageInFlowThread
                canReply={canReply}
                isThreadDetail={isThreadDetail}
                messages={!isThreadDetail ? childComments.slice(0, 2) : childComments}
                messageId={message.id}
                isReadOnly={isReadOnly}
                creatorSlug={creatorSlug}
                productSlug={productSlug || ''}
                replyInline={replyInline}
                onPostNewMessage={onPostNewMessage}
                person={person}
                sendAsCreator={isCreator}
                isWorkflowComment={isWorkflowComment}
              >
                {[...(!isThreadDetail ? childComments.slice(0, 2) : childComments)].reverse().map((subComment) => (
                  <ConversationMessage
                    isThreadComment
                    isThreadDetail={isThreadDetail}
                    key={subComment.id}
                    message={subComment}
                    creatorPersonId={creatorPersonId}
                    customerId={customerId}
                    onDeleteMessage={() => handleDeleteSubComment(message.id)(subComment.id)}
                    creatorSlug={creatorSlug}
                    product={product}
                    productSlug={productSlug}
                    refetchMessage={refetchMessage}
                    canDeleteMessages={canDeleteMessages}
                    canEditMessages={canEditMessages}
                    canPinMessages={canPinMessages}
                    canReplyToMessages={isCreator && canReply}
                    setCurrentMessage={setCurrentMessage}
                    currentMessage={currentMessage}
                    isWorkflowComment={isWorkflowComment}
                    onCloseThread={onCloseThread}
                  />
                ))}
                {fetchingState.isFetching && (
                  <div className={s['comment-msg__reply-shimmer']}>
                    <Shimmer message='Posting Reply' rounded={true} />
                  </div>
                )}
              </ConversationMessageInFlowThread>
            </>
          )}

        {isThreadDetail && canReply && (!message.isContentHidden || message.canReply) && (
          <div
            style={{
              height: !fetchingState.isFetching && !message.canReply ? 480 : undefined,
              margin: '24px 0',
              position: 'relative',
            }}
          >
            <ConversationComposeBox
              showAddVideo
              onSend={handleSend}
              showProTips={false}
              isFetching={fetchingState.isFetching}
              placeholder={messageId ? 'Type your reply' : 'Type your message'}
              isDisabled={!message.canReply}
              showAddHandbook={isCreator}
              isLoomEnabled={isCreator}
              referenceType={'comment-message'}
              referenceId={message.id}
            />
            {!fetchingState.isFetching && !message.canReply && (
              <>
                <Spacer />
                <BroadcastLock
                  product={product}
                  creatorSlug={creatorSlug}
                  hasFreeAccess={message.isAvailableForFree && message.canReplyForFree}
                  upgradeTiers={message.upgradeTiers || []}
                  isCommunityPost={message.owner?.id !== creatorPersonId}
                />
              </>
            )}
          </div>
        )}
      </div>
      <ContextMenuContent>
        {menuActions.map((action) => (
          <ContextMenuItem key={action.label} onClick={action.onClick} variant={action.variant}>
            <Icon
              icon={action.isPending ? 'ph--circle-notch--regular' : action.icon}
              className={action.isPending ? 'animate-spin' : undefined}
              size={5}
            />
            <span>{action.label}</span>
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
};
